import React, { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import { mentorApi } from '../../services/api';

const verifySchema = z.object({
  code: z.number().int().positive('Please enter a valid verification code'),
});

type VerifyData = z.infer<typeof verifySchema>;

const MentorVerifyEmail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email: string | undefined = location.state?.email;

  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerifyData>({
    resolver: zodResolver(verifySchema),
  });

  const onSubmit = async (data: VerifyData) => {
    if (!email) {
      setError('Missing email context. Please submit your application again.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await mentorApi.verifyEmail(email, data.code);
      if (response.data?.success) {
        setVerified(true);
        setSuccess('Email verified successfully! Our team will review your application.');
      } else {
        setError(response.data?.message || 'Invalid verification code. Please try again.');
      }
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError('Missing email context. Please submit your application again.');
      return;
    }
    setIsResending(true);
    setResendSuccess(null);
    setError(null);

    try {
      const response = await mentorApi.resendVerification(email);
      if (response.data?.success) {
        setResendSuccess('A new verification code has been sent to your email.');
      } else {
        setError(response.data?.message || 'Failed to resend verification code.');
      }
    } catch (err: unknown) {
      const e = err as Error;
      setError(e.message || 'An error occurred. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col justify-center py-10 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-3xl font-extrabold text-gray-900">Verify Your Email</h2>
          <p className="mt-2 text-gray-600">
            {email ? (
              <>
                We've sent a verification code to <span className="font-medium">{email}</span>.
                <br />
                Enter the code below to confirm your mentor application.
              </>
            ) : (
              'Please enter the verification code sent to your email.'
            )}
          </p>
        </motion.div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="py-8 px-4 sm:px-10">
            {error && (
              <div className="mb-4 p-3 bg-error-50 text-error-600 text-sm rounded-md border border-error-200">
                {error}
              </div>
            )}
            {resendSuccess && (
              <div className="mb-4 p-3 bg-success-50 text-success-600 text-sm rounded-md border border-success-200">
                {resendSuccess}
              </div>
            )}

            {verified ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-success-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Pending Review</h3>
                <p className="text-gray-600 mb-6">{success}</p>
                <Button fullWidth onClick={() => navigate('/profile')}>
                  View application status
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <Input
                  id="code"
                  type="number"
                  label="Verification Code"
                  placeholder="Enter the 5-digit code"
                  error={errors.code?.message}
                  {...register('code', { valueAsNumber: true })}
                />

                <Button type="submit" fullWidth isLoading={isLoading}>
                  Verify Email
                </Button>

                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    Didn't receive the code?{' '}
                    <button
                      type="button"
                      className="font-medium text-primary-600 hover:text-primary-500"
                      onClick={handleResend}
                      disabled={isResending}
                    >
                      {isResending ? 'Sending...' : 'Resend Code'}
                    </button>
                  </p>
                  {!email && (
                    <p className="mt-2 text-sm text-gray-500">
                      <Link to="/mentor-registration" className="text-primary-600 hover:text-primary-500">
                        Go back to the application form
                      </Link>
                    </p>
                  )}
                </div>
              </form>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default MentorVerifyEmail;
